import { useMutation, useSuspenseQuery } from '@apollo/client';
import { produce } from 'immer';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { cacheUtils } from '@eluve/apollo-client';
import { AvatarWithInitials } from '@eluve/blocks';
import {
  Button,
  ColDefBuilder,
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  DataTable,
  Icon,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

import {
  addUserToUserGroupMutation,
  removeUserFromGroupMutation,
  tenantUsersFragment,
  userGroupUsersFragment,
} from './operations';

// Types

type UserRow = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  tenantId: string;
  groupId: string;
  userId: string;
};

// Private Components

type RemoveUserButtonProps = {
  groupId: string;
  id: string;
  tenantId: string;
};

const RemoveUserButton: React.FC<RemoveUserButtonProps> = ({
  groupId,
  id,
  tenantId,
}) => {
  const toast = useToast();
  const { t } = useTranslation();
  const [removeUserFromGroup] = useMutation(removeUserFromGroupMutation, {
    variables: {
      id,
      tenantId,
    },
    onError: () => toast.error(t('tenantAdmin.userGroups.removeUserFailed')),
    optimisticResponse: () => {
      return {
        deleteUserGroupUsersByPk: {
          __typename: 'UserGroupUsers' as const,
          id,
        },
      };
    },
    update: (_cache) => {
      cacheUtils.updateFragment(
        {
          fragment: userGroupUsersFragment,
          key: {
            id: groupId,
          },
        },
        (existing) => {
          if (!existing) {
            return existing;
          }

          return produce(existing, (draft) => {
            const index = draft.users.findIndex(
              (userGroupUser) => userGroupUser.id === id,
            );
            if (index !== -1) {
              draft.users.splice(index, 1);
            }
          });
        },
      );
    },
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="outline" onClick={() => removeUserFromGroup()}>
          <Icon name="Trash" size="auto" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {t('tenantAdmin.userGroups.removeUserTooltip')}
      </TooltipContent>
    </Tooltip>
  );
};

const getUserGroupUsersDataQuery = graphql(
  `
    query getUserGroupUsersData($tenantId: uuid!, $userGroupId: uuid!) {
      userGroupsByPk(id: $userGroupId, tenantId: $tenantId) {
        __typename
        id
        ...UserGroupUsers
      }
      tenantsByPk(id: $tenantId) {
        __typename
        id
        ...TenantUsers
      }
    }
  `,
  [userGroupUsersFragment, tenantUsersFragment],
);

// Public Components

export const UserGroupUsers: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const tenantId = useAssignedTenantIdFromParams();
  const { userGroupId } = useParams() as { userGroupId: string };

  const columns = useMemo(
    () =>
      new ColDefBuilder<UserRow>()
        .colDef({
          id: 'avatar',
          cell: ({ row }) => (
            <AvatarWithInitials
              id={row.original.userId}
              name={[row.original.firstName, row.original.lastName].join(' ')}
            />
          ),
        })
        .defaultSortable('firstName', t('tenantAdmin.users.firstNameLabel'))
        .defaultSortable('lastName', t('tenantAdmin.users.lastNameLabel'))
        .defaultSortable('email', t('tenantAdmin.users.emailColumn'))
        .colDef({
          id: 'actions',
          cell: ({ row }) => (
            <Stack justify="end" gap={2} direction="horizontal">
              <RemoveUserButton
                groupId={row.original.groupId}
                id={row.original.id}
                tenantId={row.original.tenantId}
              />
            </Stack>
          ),
        })
        .build(),
    [t],
  );

  const { data } = useSuspenseQuery(getUserGroupUsersDataQuery, {
    variables: {
      tenantId,
      userGroupId,
    },
  });

  const tenantUsers = data.tenantsByPk?.tenantUsers ?? [];
  const userGroupUsers = useMemo(
    () => data.userGroupsByPk?.users ?? [],
    [data?.userGroupsByPk],
  );

  const usersToAdd = tenantUsers.filter(
    (tenantUser) =>
      !userGroupUsers.some(
        (userGroupUser) =>
          userGroupUser.tenant_user.user?.id === tenantUser.user.id,
      ),
  );

  const rows = useMemo(
    () =>
      userGroupUsers.map<UserRow>((userGroupUser) => {
        const user = userGroupUser.tenant_user.user;
        return {
          id: userGroupUser.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          tenantId,
          groupId: userGroupId,
          userId: user.id,
        };
      }),
    [tenantId, userGroupId, userGroupUsers],
  );

  const [addUserToGroup] = useMutation(addUserToUserGroupMutation, {
    onError: () => toast.error(t('tenantAdmin.userGroups.addUserFailed')),
    optimisticResponse: ({ groupId, userId }) => ({
      insertUserGroupUsersOne: {
        __typename: 'UserGroupUsers' as const,
        id: 'optimistic',
        tenantId,
        groupId,
        userId,
      },
    }),
    update: (_cache, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: userGroupUsersFragment,
          key: {
            id: userGroupId,
          },
        },
        (existing) => {
          const insertedUser = data?.insertUserGroupUsersOne;
          const tenantUser = tenantUsers.find(
            (tenantUser) => tenantUser.user.id === insertedUser?.userId,
          );

          if (!existing || !insertedUser || !tenantUser) {
            return existing;
          }

          return produce(existing, (draft) => {
            draft.users.push({
              ...insertedUser,
              tenant_user: tenantUser,
            });
          });
        },
      );
    },
  });

  const handleAddUserToGroup = async (userId: string) => {
    await addUserToGroup({
      variables: {
        groupId: userGroupId,
        userId,
      },
    });
  };

  return (
    <div className="grid gap-5">
      <Stack direction="horizontal" align="center" gap={5}>
        <div className="flex-1 rounded-lg border">
          <Combobox>
            <ComboboxSelectButton
              className="w-full border-transparent hover:bg-bg-quaternary"
              size="sm"
            >
              <Stack className="p-2" gap={2} direction="horizontal">
                <Icon name="UserRoundPlus" />
                <span className="text-text-secondary">
                  {t('tenantAdmin.userGroups.selectUserToAdd')}
                </span>
              </Stack>
            </ComboboxSelectButton>
            <ComboboxDropdown
              searchPlaceholder={t('tenantAdmin.userGroups.searchUsers')}
            >
              <ComboboxEmpty>
                <Stack
                  className="p-2 text-sm text-text-tertiary"
                  justify="center"
                  gap={2}
                  direction="horizontal"
                >
                  {t('tenantAdmin.userGroups.noUsersToAdd')}
                </Stack>
              </ComboboxEmpty>
              {usersToAdd.map(({ user, userId }) => (
                <ComboboxOption
                  key={userId}
                  onSelect={() => handleAddUserToGroup(userId)}
                >
                  <Stack gap={2} direction="horizontal">
                    <AvatarWithInitials
                      id={userId}
                      name={[user.firstName, user.lastName].join(' ')}
                    />
                    <span>
                      {`${user.firstName} ${user.lastName} (${user.email})`}
                    </span>
                  </Stack>
                </ComboboxOption>
              ))}
            </ComboboxDropdown>
          </Combobox>
        </div>
      </Stack>
      <DataTable
        data={rows}
        columns={columns}
        placeholder={t('tenantAdmin.userGroups.noUsersInGroup')}
      />
    </div>
  );
};
